/**
 * Centro de Alertas Inteligentes — listado priorizado + detalle.
 *
 * Layout de dos columnas (Datadog/Palantir-style): la izquierda es la
 * lista filtrable ordenada por score, la derecha es el detalle de la
 * alerta seleccionada con descomposición de score, evidencia y SLA.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api/client";
import type { AlertaIntel, AlertaSeveridad } from "../api/mock";
import type { FiltroRealtime } from "../api/contracts";
import { Icon } from "../components/Icon";
import { Ticker } from "../components/Ticker";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";
import { useCopilot } from "../system/CopilotContext";
import { useLiveDemo } from "../system/useLiveDemo";
import { exportRowsToCsv, exportTextPdf } from "../lib/exporters";

/**
 * Filtro de la Suscripcion_Realtime del Centro de Alertas: refleja en
 * vivo la generación de Alertas y sus cambios de estado (R7.5), más las
 * anomalías que las originan (R1.5, R7.4).
 */
const FILTRO_ALERTAS: FiltroRealtime = {
  tipos: ["alerta_generada", "alerta_cambio_estado", "anomalia_detectada"],
};

const SEVERIDADES: Array<{ key: AlertaSeveridad | "todas"; label: string }> = [
  { key: "todas", label: "Todas" },
  { key: "critica", label: "Crítica" },
  { key: "alta", label: "Alta" },
  { key: "media", label: "Media" },
  { key: "baja", label: "Baja" },
];

const ESTADOS = [
  { key: "todos", label: "Todos" },
  { key: "nueva", label: "Nuevas" },
  { key: "asignada", label: "Asignadas" },
  { key: "en_inspeccion", label: "En inspección" },
  { key: "cerrada", label: "Cerradas" },
];

function timeUntil(dueIso: string): string {
  const due = new Date(dueIso).getTime();
  const now = new Date("2025-06-04T12:00:00Z").getTime();
  const diff = due - now;
  const sign = diff < 0 ? "−" : "";
  const abs = Math.abs(diff);
  const h = Math.floor(abs / 3_600_000);
  const m = Math.floor((abs % 3_600_000) / 60_000);
  return `${sign}${h}h ${m}m`;
}

function slaStateClass(progress: number, estado: string): "ok" | "warn" | "late" {
  if (estado === "cerrada") return "ok";
  if (progress >= 100) return "late";
  if (progress >= 70) return "warn";
  return "ok";
}

export function AlertCenter(): JSX.Element {
  const [sev, setSev] = useState<AlertaSeveridad | "todas">("todas");
  const [estado, setEstado] = useState<string>("todos");
  const [q, setQ] = useState<string>("");
  const [activeId, setActiveId] = useState<string | null>(null);
  /** Estado local optimista de asignación / descarte por alerta. */
  const [estadoOverrides, setEstadoOverrides] = useState<Record<string, AlertaIntel["estado"]>>({});
  const { toast } = useToast();
  // Siembra el Event_Store y arranca el stream en vivo para reflejar
  // Alertas y sus cambios de estado en el Ticker (R7.4, R7.5).
  const { listo: liveListo } = useLiveDemo();

  const alertasQuery = useQuery<AlertaIntel[]>({
    queryKey: ["alertas", sev, estado, q],
    queryFn: () => {
      const params = new URLSearchParams();
      if (sev !== "todas") params.set("severidad", sev);
      if (estado !== "todos") params.set("estado", estado);
      if (q.trim()) params.set("q", q.trim());
      const qs = params.toString();
      return api<AlertaIntel[]>(`/alertas${qs ? `?${qs}` : ""}`);
    },
    staleTime: 30_000,
  });

  const alertas = useMemo(() => {
    const base = alertasQuery.data ?? [];
    return base.map((a) =>
      estadoOverrides[a.alerta_id]
        ? { ...a, estado: estadoOverrides[a.alerta_id] }
        : a,
    );
  }, [alertasQuery.data, estadoOverrides]);
  const active = useMemo(
    () => alertas.find((a) => a.alerta_id === activeId) ?? alertas[0],
    [alertas, activeId],
  );

  function setAlertaEstado(id: string, nuevo: AlertaIntel["estado"]): void {
    setEstadoOverrides((prev) => ({ ...prev, [id]: nuevo }));
  }

  const counts = useMemo(() => {
    const out: Record<string, number> = { critica: 0, alta: 0, media: 0, baja: 0 };
    for (const a of alertas) out[a.severidad] = (out[a.severidad] ?? 0) + 1;
    return out;
  }, [alertas]);

  return (
    <>
      <PageHeader
        eyebrow="Centro de mando"
        title="Centro de Alertas Inteligentes"
        subtitle="Alertas priorizadas por score con evidencia, SLA y correlación geo-temporal."
        actions={
          <>
            <button
              className="btn"
              onClick={() => {
                const ok = exportRowsToCsv(
                  `alertas-${new Date().toISOString().slice(0, 10)}.csv`,
                  alertas.map((a) => ({
                    alerta_id: a.alerta_id,
                    tipo: a.tipo_label,
                    severidad: a.severidad,
                    score: a.score,
                    estado: a.estado,
                    dma: a.dma_codigo,
                    cliente: a.cliente_codigo,
                    volumen_estimado_m3: a.volumen_estimado_m3,
                    sla_pct: a.sla_progress_pct,
                  })),
                );
                toast(
                  ok ? `Exportadas ${alertas.length} alertas a CSV` : "No hay alertas para exportar",
                  ok ? "success" : "warn",
                );
              }}
            >
              <Icon name="report" size={13} /> Exportar
            </button>
            <button
              className="btn btn--primary"
              onClick={() => toast("Formulario de alerta manual no disponible en demo", "info")}
            >
              <Icon name="plus" size={13} /> Crear alerta manual
            </button>
          </>
        }
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Críticas</span>
          <span className="kpi__value">{counts.critica}</span>
          <span className="kpi__hint">SLA 8 h</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Altas</span>
          <span className="kpi__value">{counts.alta}</span>
          <span className="kpi__hint">SLA 24 h</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Medias</span>
          <span className="kpi__value">{counts.media}</span>
          <span className="kpi__hint">SLA 72 h</span>
        </div>
        <div className="kpi">
          <span className="kpi__label">Total visible</span>
          <span className="kpi__value">{alertas.length}</span>
          <span className="kpi__hint">filtros activos</span>
        </div>
      </div>

      <section className="card" style={{ marginBottom: 12 }}>
        <div className="card__title">
          <span>Filtros</span>
          <span className="text-muted text-mono">⌘F</span>
        </div>
        <div className="form-grid">
          <div className="form-field">
            <span>Severidad</span>
            <div className="segmented" role="tablist">
              {SEVERIDADES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="segmented__btn"
                  aria-pressed={sev === s.key}
                  onClick={() => setSev(s.key as AlertaSeveridad | "todas")}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-field">
            <span>Estado</span>
            <div className="segmented">
              {ESTADOS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="segmented__btn"
                  aria-pressed={estado === s.key}
                  onClick={() => setEstado(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <label className="form-field">
            Búsqueda
            <input
              type="search"
              placeholder="Buscar cliente, DMA, tipo…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
        </div>
      </section>

      {/* Ticker_Operacional: cambios de Alerta en vivo (≤2s) con estado de
          obsolescencia al interrumpirse la suscripción (R7.4, R7.5, R1.9). */}
      {liveListo && (
        <div style={{ marginBottom: 12 }}>
          <Ticker
            filtro={FILTRO_ALERTAS}
            titulo="Alertas en vivo · stream operacional"
            variant="compact"
            maxVisibles={25}
          />
        </div>
      )}

      <div className="alert-shell">
        <div className="alert-list">
          {alertasQuery.isLoading && (
            <div className="status-block status-block--info">Cargando alertas…</div>
          )}
          {alertas.length === 0 && !alertasQuery.isLoading && (
            <div className="status-block">Sin alertas que coincidan con los filtros.</div>
          )}
          {alertas.map((a) => {
            const slaClass = slaStateClass(a.sla_progress_pct, a.estado);
            return (
              <div
                key={a.alerta_id}
                className="alert-row"
                data-active={(active?.alerta_id ?? "") === a.alerta_id}
                onClick={() => setActiveId(a.alerta_id)}
              >
                <div className={`alert-score alert-score--${a.severidad}`}>
                  {a.score}
                </div>
                <div>
                  <div className="alert-row__title">{a.tipo_label}</div>
                  <div className="alert-row__meta">
                    <span className={`sev-pill sev-pill--${a.severidad}`}>{a.severidad}</span>
                    <span className="text-mono">{a.cliente_codigo}</span>
                    <span className="text-muted">·</span>
                    <span>{a.dma_codigo}</span>
                    <span className="text-muted">·</span>
                    <span>~{a.volumen_estimado_m3} m³</span>
                    {a.incidente_id && (
                      <span className="badge badge--magenta">Incidente</span>
                    )}
                    {a.reincidencia_count > 0 && (
                      <span className="badge badge--warn">
                        Reinc. ×{a.reincidencia_count}
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div className="sla-bar">
                      <div
                        className={`sla-bar__fill sla-bar__fill--${slaClass}`}
                        style={{ width: `${Math.min(100, a.sla_progress_pct)}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="alert-row__sla">
                  <div className={slaClass === "late" ? "text-strong" : ""}>
                    {timeUntil(a.due_at)}
                  </div>
                  <div className="text-muted" style={{ marginTop: 2 }}>
                    SLA {a.sla_progress_pct}%
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {active && (
          <AlertDetail
            alerta={active}
            onAsignar={(id) => {
              setAlertaEstado(id, "asignada");
              toast(`Alerta ${id.slice(-4)} asignada a cuadrilla`, "success");
            }}
            onDescartar={(id) => {
              setAlertaEstado(id, "cerrada");
              toast(`Alerta ${id.slice(-4)} descartada`, "warn");
            }}
          />
        )}
      </div>
    </>
  );
}

function AlertDetail({
  alerta,
  onAsignar,
  onDescartar,
}: {
  alerta: AlertaIntel;
  onAsignar: (id: string) => void;
  onDescartar: (id: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const { openCopilot } = useCopilot();
  const { toast } = useToast();
  const serie = alerta.evidencia.serie.map((v, i) => ({ idx: i + 1, m3: v }));
  const slaClass = slaStateClass(alerta.sla_progress_pct, alerta.estado);
  return (
    <aside className="alert-detail">
      <div>
        <span className={`sev-pill sev-pill--${alerta.severidad}`}>{alerta.severidad}</span>
        <h3 style={{ marginTop: 6, marginBottom: 4 }}>{alerta.tipo_label}</h3>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {alerta.dma_codigo} · {alerta.cliente_codigo} · {alerta.direccion}
        </div>
      </div>

      <div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <div>
            <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
              Score
            </div>
            <div
              className={`alert-score alert-score--${alerta.severidad}`}
              style={{ fontSize: 30, marginTop: 2, textAlign: "left" }}
            >
              {alerta.score}
            </div>
          </div>
          <div>
            <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase" }}>
              Volumen estimado
            </div>
            <div className="text-mono" style={{ fontSize: 16 }}>
              {alerta.volumen_estimado_m3} m³
            </div>
          </div>
        </div>
        <div className="sla-bar">
          <div
            className={`sla-bar__fill sla-bar__fill--${slaClass}`}
            style={{ width: `${Math.min(100, alerta.sla_progress_pct)}%` }}
          />
        </div>
        <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
          SLA {alerta.sla_progress_pct}% · vence en {timeUntil(alerta.due_at)}
        </div>
      </div>

      <div>
        <div className="card__title">Composición del score</div>
        {alerta.components.map((c) => (
          <div key={c.key} className="alert-component">
            <span style={{ textTransform: "capitalize" }}>{c.label}</span>
            <strong>
              {c.value} · w {c.weight.toFixed(2)}
            </strong>
          </div>
        ))}
      </div>

      <div>
        <div className="card__title">Evidencia · serie reciente</div>
        <div style={{ width: "100%", height: 110 }}>
          <ResponsiveContainer>
            <AreaChart data={serie} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
              <defs>
                <linearGradient id="grad-evidencia" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--c-primary-300)" stopOpacity={0.55} />
                  <stop offset="95%" stopColor="var(--c-primary-300)" stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
              <XAxis dataKey="idx" hide />
              <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{
                  background: "var(--c-surface)",
                  border: "1px solid var(--c-border)",
                  borderRadius: 6,
                  fontSize: 11,
                }}
              />
              {alerta.evidencia.umbral !== undefined && (
                <ReferenceLine
                  y={alerta.evidencia.umbral}
                  stroke="var(--c-warn)"
                  strokeDasharray="3 3"
                />
              )}
              <Area
                type="monotone"
                dataKey="m3"
                stroke="var(--c-primary-300)"
                strokeWidth={2}
                fill="url(#grad-evidencia)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {alerta.evidencia.presion_setpoint_bar !== undefined && (
        <div>
          <div className="card__title">Presión</div>
          <div className="alert-component">
            <span>Setpoint PRV</span>
            <strong>{alerta.evidencia.presion_setpoint_bar.toFixed(2)} bar</strong>
          </div>
          <div className="alert-component">
            <span>Observada</span>
            <strong>{(alerta.evidencia.presion_observada_bar ?? 0).toFixed(2)} bar</strong>
          </div>
        </div>
      )}

      <div className="hstack" style={{ flexWrap: "wrap" }}>
        {alerta.estado === "nueva" && (
          <button className="btn btn--primary" onClick={() => onAsignar(alerta.alerta_id)}>
            <Icon name="wrench" size={13} /> Asignar a cuadrilla
          </button>
        )}
        {alerta.estado === "asignada" && (
          <button
            className="btn"
            onClick={() => navigate(`/mapa?dma=${alerta.dma_codigo}`)}
          >
            <Icon name="search" size={13} /> Ver en mapa
          </button>
        )}
        <button
          className="btn btn--ghost"
          onClick={() => {
            exportTextPdf(
              `evidencia-${alerta.alerta_id.slice(-6)}.pdf`,
              `Evidencia alerta ${alerta.tipo_label}`,
              [
                `DMA: ${alerta.dma_codigo}`,
                `Cliente: ${alerta.cliente_codigo}`,
                `Direccion: ${alerta.direccion}`,
                `Severidad: ${alerta.severidad} · score ${alerta.score}`,
                `Volumen estimado: ${alerta.volumen_estimado_m3} m3`,
                `SLA: ${alerta.sla_progress_pct}%`,
                "",
                "Serie de evidencia (m3):",
                `  ${alerta.evidencia.serie.map((v) => v.toFixed(1)).join(", ")}`,
              ],
            );
            toast(`Evidencia de ${alerta.alerta_id.slice(-4)} exportada`, "success");
          }}
        >
          <Icon name="report" size={13} /> Exportar evidencia
        </button>
        <button
          className="btn btn--ghost"
          onClick={() =>
            openCopilot(`Explica la alerta de ${alerta.tipo_label} en ${alerta.dma_codigo}`)
          }
        >
          <Icon name="flask" size={13} /> Copilot
        </button>
        {alerta.estado !== "cerrada" && (
          <button className="btn btn--danger" onClick={() => onDescartar(alerta.alerta_id)}>
            <Icon name="logout" size={13} /> Descartar
          </button>
        )}
      </div>
    </aside>
  );
}
